/**
 * <store-availability> — "Available at N stores" badge + dealer modal (progressive enhancement).
 *
 * Flow (production): ask ValerydDealer.country() (assets/dealer-context.js) whose dealers to
 * show. That is the market's pinned country, or the visitor's own when the fallback list names
 * it. With no country the component stays hidden and asks nothing. With one it fetches
 * per-dealer stock from the App Proxy (/apps/stock/stores), reveals its controls and fills a
 * native <dialog> (snippets/dealer-stock-dialog.liquid) with the stores: a card per store with
 * the chain's logo, the partner level's emblem, the quantity, a call link and directions;
 * search by town or postcode; nearest first when the browser gives a position, else by level.
 *
 * Two surfaces, both optional in the markup:
 *   [data-dealer-summary] "Available at 143 dealers" beside the price: the network as the
 *                         claim, no named stores before we know where the visitor is.
 *   [data-dealer-trigger] opens the dialog with the full in-stock list.
 *
 * With `data-on-demand` (snippets/dealer-stock-host.liquid) there is no part of its own: any
 * `[data-dealer-stock-open][data-artno]` on the page — a product card's dealer count — opens
 * the dialog for that part, and the stores are fetched on the click.
 *
 * NO SOAP, credentials or store data live here — that is the valeryd-stock-proxy middleware.
 * The badge stays hidden until stock is confirmed, so the block adds nothing when there is
 * none. Built with DOM nodes + textContent (no innerHTML) so dealer data can't inject markup.
 */
if (!customElements.get('store-availability')) {
  const TIER_RANK = { platina: 0, guld: 1, silver: 2 };
  // The first page of cards; the rest wait behind "Show all", so a phone does not lay out 140.
  const PAGE = 30;
  // Without this, a proxy that accepts the connection and never answers leaves init()
  // awaiting forever. init()'s catch is deliberately silent and the component stays
  // hidden, so a timeout only reaches that state sooner.
  const STORES_TIMEOUT_MS = 8000;

  // Engines without AbortSignal.timeout keep the previous unbounded behaviour rather than
  // throwing while the fetch options are being built.
  function timeoutSignal(ms) {
    try {
      return AbortSignal.timeout(ms);
    } catch (e) {
      return undefined;
    }
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function byTier(a, b) {
    return (TIER_RANK[a.tier] ?? 9) - (TIER_RANK[b.tier] ?? 9) || (a.scoringIndex ?? 1e9) - (b.scoringIndex ?? 1e9);
  }

  // Straight-line distance in km; good enough to order stores by.
  function km(a, b) {
    const rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad;
    const dLng = (b.lng - a.lng) * rad;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
    return 12742 * Math.asin(Math.sqrt(h));
  }

  customElements.define(
    'store-availability',
    class StoreAvailability extends HTMLElement {
      connectedCallback() {
        // Every trigger, not just the first: a page may carry more than one way into the list.
        this.triggers = Array.from(this.querySelectorAll('[data-dealer-trigger]'));
        this.trigger = this.triggers[0];
        this.summaryEl = this.querySelector('[data-dealer-summary]');
        this.badgeEl = this.querySelector('[data-dealer-badge]');
        this.modal = this.querySelector('[data-dealer-modal]');
        this.searchEl = this.querySelector('[data-dealer-search]');
        this.countEl = this.querySelector('[data-dealer-count-line]');
        this.listEl = this.querySelector('[data-dealer-list]');
        this.artno = this.dataset.artno;
        this.onDemand = this.hasAttribute('data-on-demand');
        this.useMock = this.dataset.useMock === 'true';
        this.stores = [];
        this.origin = null;
        if (!this.modal || !this.listEl) return;
        if (!this.onDemand && (!this.trigger || !this.artno)) return;

        this.onTrigger = () => this.open();
        this.onSearch = () => {
          this.listOpen = false;
          this.renderList();
        };
        // A dealer chosen in one place changes every panel on the page.
        this.onDealerChange = () => {
          if (this.modal && this.modal.open) this.renderList();
        };
        this.onPageClick = (event) => {
          const opener = event.target.closest && event.target.closest('[data-dealer-stock-open][data-artno]');
          if (!opener) return;
          event.preventDefault();
          this.openFor(opener.dataset.artno);
        };
        document.addEventListener('dealer:changed', this.onDealerChange);
        this.triggers.forEach((t) => t.addEventListener('click', this.onTrigger));
        if (this.searchEl) this.searchEl.addEventListener('input', this.onSearch);

        if (this.onDemand) document.addEventListener('click', this.onPageClick);
        else this.init();
      }

      disconnectedCallback() {
        (this.triggers || []).forEach((t) => t.removeEventListener('click', this.onTrigger));
        document.removeEventListener('dealer:changed', this.onDealerChange);
        document.removeEventListener('click', this.onPageClick);
        if (this.searchEl) this.searchEl.removeEventListener('input', this.onSearch);
        if (this.modal && this.modal.open) this.modal.close();
      }

      async init() {
        try {
          const country = await this.resolveCountry();
          if (!country) return; // no dealers for this page → stay hidden
          const data = await this.fetchStores(country);
          if (!data || data.supported === false) return;
          const all = Array.isArray(data.stores) ? data.stores : [];
          this.stores = all.filter((s) => s.inStock);
          const count = typeof data.storeCount === 'number' ? data.storeCount : this.stores.length;
          // No dealer stock, or a count with no dealer we can name: add nothing. The proxy counts
          // stock from the stock system but lists only dealers in its register, and a register
          // can be empty (Norway's is, today) — revealing the controls then opens a dialog that
          // says no store matches.
          if (count <= 0 || !this.stores.length) return;
          this.showBadge(count);
        } catch (e) {
          // Silent: availability is an enhancement — never break the product page.
        }
      }

      // The page-wide dialog: open at once with a loading line, then fill it for this part. A
      // second click before the first answer wins — the earlier answer is dropped.
      async openFor(artno) {
        const ticket = (this.ticket = (this.ticket || 0) + 1);
        this.artno = artno;
        this.stores = [];
        this.reset();
        this.status(this.dataset.loading);
        this.show();
        let failed = false;
        try {
          const country = await this.resolveCountry();
          const data = country ? await this.fetchStores(country) : null;
          if (!data || data.supported === false) failed = true;
          else this.stores = (Array.isArray(data.stores) ? data.stores : []).filter((s) => s.inStock);
        } catch (e) {
          failed = true;
        }
        if (ticket !== this.ticket) return;
        if (failed) this.status(this.dataset.error);
        else this.renderList();
      }

      // Whose dealers: ValerydDealer.country() owns the rule, and null means stay hidden. There
      // is no fallback country. Falling back to SE is how a German shopper whose lookup timed out
      // used to be shown Swedish dealers. The /stores response's own `supported` flag still gates
      // what comes back. Mock skips the rule, because its fixture answers whatever is asked.
      async resolveCountry() {
        if (this.useMock) return this.dataset.dealerCountry || 'SE';
        return window.ValerydDealer ? window.ValerydDealer.country(this) : null;
      }

      async fetchStores(country) {
        const base = this.dataset.endpoint;
        const sep = base.indexOf('?') === -1 ? '?' : '&';
        const url = this.useMock
          ? base
          : base + sep + 'artno=' + encodeURIComponent(this.artno) + '&country=' + encodeURIComponent(country);
        const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: timeoutSignal(STORES_TIMEOUT_MS) });
        if (!res.ok) return null;
        return res.json();
      }

      badgeText(count) {
        if (count === 1 && this.dataset.badgeOne) return this.dataset.badgeOne;
        return (this.dataset.badge || '%COUNT%').replace('%COUNT%', count);
      }

      showBadge(count) {
        if (this.badgeEl) this.badgeEl.textContent = this.badgeText(count);
        this.showSummary(count);
        this.triggers.forEach((t) => {
          t.hidden = false;
        });
      }

      // "Available at 143 dealers" beside the price, the number set in bold. The one-form is
      // rendered with its number in place by Liquid, so it is shown as it is.
      showSummary(count) {
        const box = this.summaryEl;
        const text = box && box.querySelector('[data-dealer-summary-text]');
        if (!text) return;
        if (count === 1 && this.dataset.atDealersOne) {
          text.textContent = this.dataset.atDealersOne;
        } else {
          const [before, after] = (this.dataset.atDealers || '%COUNT%').split('%COUNT%');
          text.replaceChildren(before, el('strong', 'dealer-avail__summary-n', String(count)), after || '');
        }
        box.hidden = false;
      }

      open() {
        this.reset();
        this.renderList();
        this.show();
      }

      show() {
        if (this.modal.open) return;
        if (typeof this.modal.showModal === 'function') this.modal.showModal();
        else this.modal.setAttribute('open', '');
      }

      // Every opening starts from the full list, nearest first.
      reset() {
        if (this.searchEl) this.searchEl.value = '';
        this.listOpen = false;
        this.loadChains();
        this.locate();
      }

      // One line in place of the list: loading, or the proxy could not answer.
      status(text) {
        if (this.countEl) this.countEl.textContent = '';
        const li = el('li', 'dealer-avail__status', text || '');
        this.listEl.replaceChildren(li);
      }

      // The list is nearest first, the one order (Hubert, 2026-11-03). The browser is asked for a
      // position once per page; refused or unavailable, the list quietly stays in level order.
      locate() {
        const geo = window.navigator && window.navigator.geolocation;
        if (this.origin || this.locating || !geo) return;
        this.locating = true;
        geo.getCurrentPosition(
          (pos) => {
            this.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            if (this.modal.open && this.stores.length) this.renderList();
          },
          () => {},
          { timeout: 10000, maximumAge: 600000 }
        );
      }

      // The chain logos are matched on the store's name (assets/dealer-chains.json); fetched once
      // per page, the first time a dialog opens. A failed fetch just means no logos.
      loadChains() {
        if (this.chains || !this.dataset.chainsUrl) return;
        const url = this.dataset.chainsUrl;
        this.chains = fetch(url)
          .then((res) => (res.ok ? res.json() : []))
          .then((list) => list.map((c) => ({ match: String(c.match).toLowerCase(), src: new URL(c.logo, new URL(url, location.href)).href })))
          .catch(() => []);
        this.chains.then((list) => {
          this.chainList = list;
          if (this.modal.open && this.stores.length) this.renderList();
        });
      }

      chainLogo(store) {
        const name = (store.name || '').toLowerCase();
        const hit = (this.chainList || []).find((c) => name.includes(c.match));
        return hit ? hit.src : null;
      }

      filtered() {
        const q = (this.searchEl && this.searchEl.value.trim().toLowerCase()) || '';
        if (!q) return this.stores;
        return this.stores.filter((s) => ((s.city || '') + ' ' + (s.postcode || '')).toLowerCase().indexOf(q) !== -1);
      }

      sorted(rows) {
        return rows.slice().sort((a, b) => this.byPlace(a, b));
      }

      // Nearest first when there is a position, partner level otherwise.
      byPlace(a, b) {
        if (this.origin) {
          const d = (s) => (s.lat && s.lng ? km(this.origin, s) : Infinity);
          return d(a) - d(b) || byTier(a, b);
        }
        return byTier(a, b);
      }

      renderCount(n) {
        if (!this.countEl) return;
        this.countEl.replaceChildren(this.icon('check'));
        // The one-form is rendered with its number in place (Liquid picks the form only for a
        // real count), so it is shown as it is; the other form gets its number set in bold.
        if (n === 1 && this.dataset.countLineOne) {
          this.countEl.appendChild(document.createTextNode(this.dataset.countLineOne));
          return;
        }
        const [before, after] = String(this.dataset.countLine || '%COUNT%').split('%COUNT%');
        if (before) this.countEl.appendChild(document.createTextNode(before));
        this.countEl.appendChild(el('strong', 'dealer-avail__count-n', String(n)));
        if (after) this.countEl.appendChild(document.createTextNode(after));
      }

      renderList() {
        this.renderCount(this.stores.length);
        const rows = this.sorted(this.filtered());
        this.listEl.replaceChildren();
        if (!rows.length) {
          this.listEl.appendChild(el('li', 'dealer-avail__status', this.dataset.noMatch || ''));
          return;
        }
        const frag = document.createDocumentFragment();
        const shown = this.listOpen ? rows : rows.slice(0, PAGE);
        shown.forEach((s) => frag.appendChild(this.row(s)));
        if (shown.length < rows.length) {
          const li = el('li', 'dealer-avail__more-row');
          const more = el('button', 'dealer-avail__more', String(this.dataset.showAll || '%COUNT%').replace('%COUNT%', String(rows.length)));
          more.type = 'button';
          more.addEventListener('click', () => {
            this.listOpen = true;
            this.renderList();
          });
          li.appendChild(more);
          frag.appendChild(li);
        }
        this.listEl.appendChild(frag);
      }

      // One store as a card: who and where on top, with the chain's logo and the level's emblem;
      // the stock and what to do about it underneath.
      row(store) {
        const li = el('li', 'dealer-avail__row');

        const top = el('div', 'dealer-avail__top');
        const logo = this.chainLogo(store);
        if (logo) {
          const img = el('img', 'dealer-avail__logo');
          img.src = logo;
          img.alt = '';
          img.width = 40;
          img.height = 40;
          img.loading = 'lazy';
          top.appendChild(img);
        }
        const who = el('div', 'dealer-avail__who');
        who.appendChild(el('span', 'dealer-avail__name', store.name || ''));
        const where = [store.postcode, store.city].filter(Boolean).join(' ');
        const meta = el('span', 'dealer-avail__meta', where);
        if (this.origin && store.lat && store.lng) {
          meta.appendChild(el('span', 'dealer-avail__distance', Math.round(km(this.origin, store)) + ' km'));
        }
        who.appendChild(meta);
        // Choosing a dealer is the first step of the collect flow: it pins them to the top of
        // every availability panel and is what <collect-list> checks stock against. A quiet link
        // under the address, so the band below keeps stock and directions on one line.
        if (this.dataset.choose) {
          const chosen = window.ValerydDealer && window.ValerydDealer.read();
          const isChosen = chosen && chosen.dealer.name === store.name && chosen.dealer.city === store.city;
          const pick = el('button', 'dealer-avail__choose', isChosen ? this.dataset.chosen || '' : this.dataset.choose);
          pick.type = 'button';
          if (isChosen) pick.setAttribute('aria-current', 'true');
          pick.addEventListener('click', () => {
            if (window.ValerydDealer) window.ValerydDealer.choose(store);
            if (this.modal && this.modal.open) this.modal.close();
          });
          who.appendChild(pick);
        }
        top.appendChild(who);
        const tier = (store.tier || '').toLowerCase();
        const key = tier.charAt(0).toUpperCase() + tier.slice(1);
        if (tier && this.dataset['tier' + key + 'Img']) {
          const emblem = el('img', 'dealer-avail__emblem');
          emblem.src = this.dataset['tier' + key + 'Img'];
          emblem.alt = this.dataset['tier' + key] || '';
          emblem.title = emblem.alt;
          emblem.width = 52;
          emblem.height = 52;
          emblem.loading = 'lazy';
          top.appendChild(emblem);
        }
        li.appendChild(top);

        const bottom = el('div', 'dealer-avail__bottom');
        const qty = el('span', 'dealer-avail__qty');
        qty.appendChild(this.icon('check'));
        qty.appendChild(el('span', '', (this.dataset.instock || '%COUNT%').replace('%COUNT%', store.quantity != null ? store.quantity : 0)));
        bottom.appendChild(qty);

        const actions = el('span', 'dealer-avail__actions');
        if (store.phone) {
          const call = this.link('tel:' + store.phone.replace(/[^\d+]/g, ''), this.dataset.contact, 'dealer-avail__action');
          call.prepend(this.icon('phone'));
          actions.appendChild(call);
        }
        const dir = this.directionsUrl(store);
        if (dir) {
          const a = this.link(dir, this.dataset.directions, 'dealer-avail__route');
          a.target = '_blank';
          a.rel = 'noopener';
          a.appendChild(this.icon('chevron-right'));
          actions.appendChild(a);
        }
        if (actions.childNodes.length) bottom.appendChild(actions);
        li.appendChild(bottom);
        return li;
      }

      // An icon from the theme's set, parked in a <template> by the dialog snippet.
      icon(name) {
        const tpl = this.querySelector('template[data-icon="' + name + '"]');
        return tpl ? tpl.content.cloneNode(true) : document.createTextNode('');
      }

      directionsUrl(store) {
        if (store.lat && store.lng) {
          return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(store.lat + ',' + store.lng);
        }
        const dest = [store.name, store.postcode, store.city].filter(Boolean).join(' ');
        return dest ? 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest) : null;
      }

      link(href, text, className) {
        const a = el('a', className);
        a.href = href;
        a.appendChild(el('span', '', text || ''));
        return a;
      }
    }
  );
}
